import { useState, useCallback } from 'react';
import { validatePin } from 'api/clubApi';

/**
 * Custom hook for PIN validation before executing actions
 * @param {string} clubId - The club ID for PIN validation
 * @returns {object} - Hook interface with state and methods
 */
export const usePinValidation = (clubId) => {
  const [showPinInput, setShowPinInput] = useState(false);
  const [pinAction, setPinAction] = useState('');
  const [pinDescription, setPinDescription] = useState(''); // Add description state
  const [pendingAction, setPendingAction] = useState(null);
  const [pinValidationLoading, setPinValidationLoading] = useState(false);

  /**
   * Request PIN validation before executing an action
   * @param {string} actionType - Identifier for the action being performed
   * @param {function} actionCallback - Function to execute after successful PIN validation
   * @param {object} actionData - Optional data to pass to the callback
   * @param {string} description - Optional description text to show in PIN modal
   */
  const requestPinValidation = useCallback(
    (actionType, actionCallback, actionData = null, description = '') => {
      setPinAction(actionType);
      setPinDescription(description); // Store the description
      setPendingAction({ callback: actionCallback, data: actionData });
      setShowPinInput(true);
    },
    []
  );

  /**
   * Handle PIN confirmation - validates PIN on server and executes pending action
   * @param {object} pinData - Object containing the entered PIN
   */
  const handlePinConfirmed = useCallback(
    async (pinData) => {
      if (!pendingAction || !clubId) return;

      setPinValidationLoading(true);
      try {
        // Server-side validation - the ONLY way to validate PINs
        await validatePin(clubId, pinData.pin);

        setShowPinInput(false);

        // Execute the pending action
        if (pendingAction.callback) {
          await pendingAction.callback(pendingAction.data, pinData.pin);
        }

        // Clear pending action and description
        setPendingAction(null);
        setPinAction('');
        setPinDescription(''); // Clear description
      } catch (error) {
        console.error('PIN validation error:', error);

        // Extract error message from server response
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data ||
          error.message ||
          'Invalid PIN';

        const finalError = new Error(errorMessage);
        throw finalError;
      } finally {
        setPinValidationLoading(false);
      }
    },
    [pendingAction, clubId]
  );

  /**
   * Cancel PIN input and clear pending action
   */
  const cancelPinInput = useCallback(() => {
    setShowPinInput(false);
    setPendingAction(null);
    setPinAction('');
    setPinDescription(''); // Clear description when canceling
  }, []);

  return {
    // State
    showPinInput,
    pinAction,
    pinDescription, // Return description
    pinValidationLoading,

    // Methods
    requestPinValidation,
    handlePinConfirmed,
    cancelPinInput,
  };
};
